import React, { useEffect, useState, useCallback } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { voucherAPI, pdfAPI } from '../lib/api';
import { StatusBadge } from './DashboardPage';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { ArrowLeft, Printer, XCircle, CheckCircle, FileText, Upload } from 'lucide-react';
import SignaturePad from '../components/SignaturePad';
import BillUpload from '../components/BillUpload';

export default function VoucherDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [voucher, setVoucher] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showBillUpload, setShowBillUpload] = useState(false);
  const [newBills, setNewBills] = useState([]);

  const fetchVoucher = useCallback(async () => {
    try {
      const res = await voucherAPI.get(id);
      setVoucher(res.data.voucher);
    } catch (err) {
      toast.error('Failed to load voucher');
      navigate('/vouchers');
    } finally {
      setLoading(false);
    }
  }, [id, navigate]);

  useEffect(() => { fetchVoucher(); }, [fetchVoucher]);

  const handleApprove = async () => {
    try {
      await voucherAPI.approve(id);
      toast.success('Voucher approved');
      fetchVoucher();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to approve');
    }
  };

  const handleReject = async () => {
    const reason = window.prompt('Rejection reason:');
    if (reason === null) return;
    try {
      await voucherAPI.reject(id, reason);
      toast.success('Voucher rejected');
      fetchVoucher();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to reject');
    }
  };

  const handleCancel = async () => {
    if (!window.confirm('Cancel this voucher?')) return;
    try {
      await voucherAPI.cancel(id, 'Cancelled by user');
      toast.success('Voucher cancelled');
      fetchVoucher();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to cancel');
    }
  };

  const handlePrint = async () => {
    try {
      const res = await pdfAPI.getVoucherPDF(id);
      const blob = new Blob([res.data.html], { type: 'text/html' });
      const url = URL.createObjectURL(blob);
      const printWindow = window.open(url, '_blank');
      printWindow.addEventListener('load', () => {
        printWindow.print();
        URL.revokeObjectURL(url);
      });
    } catch (err) {
      toast.error('Failed to generate PDF');
    }
  };

  const handleUploadBills = async () => {
    if (newBills.length === 0) { toast.error('Select files first'); return; }
    try {
      await voucherAPI.uploadBills(id, newBills.map(f => ({
        filename: f.filename, originalName: f.originalName,
        mimetype: f.mimetype, size: f.size, data: f.data
      })));
      toast.success('Bills uploaded');
      setNewBills([]);
      setShowBillUpload(false);
      fetchVoucher();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Upload failed');
    }
  };

  const handleSaveSignature = async (role, signatureData) => {
    try {
      await voucherAPI.saveSignature(id, role, signatureData);
      toast.success('Signature saved');
      fetchVoucher();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to save signature');
    }
  };

  if (loading) return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-6 w-6 border-b-2 border-slate-900"></div></div>;
  if (!voucher) return <p className="text-center text-slate-500 py-8">Voucher not found</p>;

  const canApprove = ['admin', 'manager'].includes(user?.role) && voucher.status === 'Pending' && voucher.createdBy?._id !== user?.id;
  const canCancel = voucher.status !== 'Settled' && voucher.status !== 'Cancelled' &&
    (voucher.createdBy?._id === user?.id || user?.role === 'admin');

  return (
    <div className="max-w-3xl mx-auto space-y-4" data-testid="voucher-detail-page">
      <VoucherActions
        canApprove={canApprove}
        canCancel={canCancel}
        onApprove={handleApprove}
        onReject={handleReject}
        onCancel={handleCancel}
        onPrint={handlePrint}
      />
      <div className="bg-white border border-slate-200 rounded-lg">
        <VoucherHeader voucher={voucher} />
        <VoucherDetails voucher={voucher} />
        <VoucherFlags flags={voucher.flags} />
        <VoucherItems items={voucher.items} />
        {voucher.notes && (
          <div className="px-4 pb-4">
            <p className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider mb-1">Notes</p>
            <p className="text-xs text-slate-600">{voucher.notes}</p>
          </div>
        )}
        <VoucherBills
          bills={voucher.bills}
          status={voucher.status}
          showUpload={showBillUpload}
          onToggleUpload={() => setShowBillUpload(!showBillUpload)}
          newBills={newBills}
          setNewBills={setNewBills}
          onUpload={handleUploadBills}
        />
        <VoucherSignatures
          voucher={voucher}
          userId={user?.id}
          onSaveSignature={handleSaveSignature}
        />
        <VoucherAuditTrail auditLog={voucher.auditLog} />
      </div>
    </div>
  );
}

/* --- Sub-components --- */

function VoucherActions({ canApprove, canCancel, onApprove, onReject, onCancel, onPrint }) {
  return (
    <div className="flex items-center justify-between">
      <Link to="/vouchers" className="flex items-center gap-1 text-xs text-slate-500 hover:text-slate-900" data-testid="back-to-vouchers">
        <ArrowLeft className="w-3.5 h-3.5" /> Back to Vouchers
      </Link>
      <div className="flex items-center gap-2">
        {canApprove && (
          <>
            <Button size="sm" onClick={onApprove} className="bg-green-600 hover:bg-green-700 text-white text-xs h-7 px-2" data-testid="approve-btn">
              <CheckCircle className="w-3.5 h-3.5 mr-1" /> Approve
            </Button>
            <Button size="sm" onClick={onReject} variant="destructive" className="text-xs h-7 px-2" data-testid="reject-btn">
              <XCircle className="w-3.5 h-3.5 mr-1" /> Reject
            </Button>
          </>
        )}
        {canCancel && (
          <Button size="sm" variant="outline" onClick={onCancel} className="text-xs h-7 px-2" data-testid="cancel-voucher-btn">Cancel</Button>
        )}
        <Button size="sm" variant="outline" onClick={onPrint} className="text-xs h-7 px-2" data-testid="print-voucher-btn">
          <Printer className="w-3.5 h-3.5 mr-1" /> Print
        </Button>
      </div>
    </div>
  );
}

function VoucherHeader({ voucher }) {
  return (
    <>
      <div className="flex items-center justify-between p-4 border-b border-slate-200">
        <div>
          <p className="text-xs text-slate-500">{voucher.voucherType}</p>
          <h2 className="text-lg font-bold text-slate-900">{voucher.voucherId}</h2>
        </div>
        <StatusBadge status={voucher.status} />
      </div>
      <div className="bg-slate-50 border-b border-slate-200 p-4">
        <p className="text-2xl font-bold text-slate-900">₹{voucher.amount?.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</p>
        <p className="text-xs text-slate-500 italic mt-0.5">{voucher.amountInWords}</p>
      </div>
    </>
  );
}

const formatDate = (d) => d ? new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '-';

function Field({ label, value }) {
  return (
    <div>
      <p className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider">{label}</p>
      <p className="text-sm font-medium text-slate-900">{value}</p>
    </div>
  );
}

function VoucherDetails({ voucher }) {
  return (
    <div className="p-4 grid grid-cols-2 md:grid-cols-3 gap-4">
      <Field label="Date" value={formatDate(voucher.date)} />
      <Field label="Payment Mode" value={voucher.paymentMode} />
      <Field label="Transaction Ref" value={voucher.transactionRef || '-'} />
      <Field label="Category" value={voucher.category?.name || '-'} />
      <Field label="Created By" value={voucher.createdBy?.name || '-'} />
      <Field label="Approved By" value={voucher.approvedBy?.name || 'Pending'} />
      <div className="col-span-2 md:col-span-3">
        <p className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider mb-1">Description</p>
        <p className="text-sm text-slate-700">{voucher.description}</p>
      </div>
    </div>
  );
}

function VoucherFlags({ flags }) {
  if (!flags?.length) return null;
  return (
    <div className="px-4 pb-4">
      <p className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider mb-1">Flags</p>
      <div className="flex gap-1.5 flex-wrap">
        {flags.map((f) => (
          <span key={f} className="text-[10px] font-medium px-2 py-0.5 rounded-full bg-amber-50 text-amber-700 border border-amber-200">{f}</span>
        ))}
      </div>
    </div>
  );
}

function VoucherItems({ items }) {
  if (!items?.length) return null;
  return (
    <div className="px-4 pb-4">
      <p className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider mb-2">Expense Breakdown</p>
      <table className="w-full text-xs border border-slate-200 rounded-md overflow-hidden">
        <thead><tr className="bg-slate-50">
          <th className="text-left px-3 py-1.5">Category</th>
          <th className="text-left px-3 py-1.5">Description</th>
          <th className="text-right px-3 py-1.5">Amount</th>
        </tr></thead>
        <tbody>
          {items.map((item) => (
            <tr key={item._id || item.description} className="border-t border-slate-100">
              <td className="px-3 py-1.5">{item.category?.name || '-'}</td>
              <td className="px-3 py-1.5">{item.description}</td>
              <td className="px-3 py-1.5 text-right font-medium">₹{item.amount?.toLocaleString('en-IN')}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function VoucherBills({ bills, status, showUpload, onToggleUpload, newBills, setNewBills, onUpload }) {
  return (
    <div className="px-4 pb-4">
      <div className="flex items-center justify-between mb-2">
        <p className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider">Bills / Receipts ({bills?.length || 0})</p>
        {status !== 'Cancelled' && (
          <button onClick={onToggleUpload} className="text-[10px] text-slate-600 hover:text-slate-900 underline" data-testid="toggle-bill-upload">
            {showUpload ? 'Cancel' : 'Upload Bills'}
          </button>
        )}
      </div>
      {bills?.length > 0 && (
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mb-2">
          {bills.map((bill) => (
            <div key={bill._id || bill.filename} className="border border-slate-200 rounded-md p-1.5 bg-slate-50" data-testid={`bill-display-${bill.filename}`}>
              {bill.data && bill.mimetype?.startsWith('image/') ? (
                <img src={bill.data} alt={bill.originalName} className="w-full h-20 object-cover rounded" />
              ) : (
                <div className="w-full h-20 flex items-center justify-center bg-slate-100 rounded"><FileText className="w-6 h-6 text-slate-400" /></div>
              )}
              <p className="text-[10px] text-slate-500 mt-1 truncate">{bill.originalName || bill.filename}</p>
            </div>
          ))}
        </div>
      )}
      {bills?.length === 0 && !showUpload && <p className="text-xs text-slate-400 italic">No bills attached</p>}
      {showUpload && (
        <div className="border border-slate-200 rounded-md p-3 bg-slate-50 space-y-2">
          <BillUpload files={newBills} onChange={setNewBills} />
          {newBills.length > 0 && (
            <Button size="sm" onClick={onUpload} className="bg-slate-900 hover:bg-slate-800 text-white text-xs" data-testid="upload-bills-btn">
              <Upload className="w-3.5 h-3.5 mr-1" /> Upload {newBills.length} file(s)
            </Button>
          )}
        </div>
      )}
    </div>
  );
}

function VoucherSignatures({ voucher, userId, onSaveSignature }) {
  const sigEntries = [
    { role: 'preparedBy', label: 'Prepared By', canSign: !voucher.signatures?.preparedBy?.signatureData && voucher.createdBy?._id === userId },
    { role: 'approvedBy', label: 'Approved By', canSign: voucher.status === 'Approved' && !voucher.signatures?.approvedBy?.signatureData && voucher.approvedBy?._id === userId },
    { role: 'receivedBy', label: 'Received By', canSign: voucher.status === 'Approved' && !voucher.signatures?.receivedBy?.signatureData },
    { role: 'paidBy', label: 'Paid By', canSign: voucher.status === 'Approved' && !voucher.signatures?.paidBy?.signatureData },
  ];
  return (
    <div className="border-t border-slate-200 px-4 py-4">
      <p className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider mb-3">Signatures</p>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {sigEntries.map(({ role, label, canSign }) => (
          <SigBlock key={role} label={label} sig={voucher.signatures?.[role]} canSign={canSign} onSave={(data) => onSaveSignature(role, data)} />
        ))}
      </div>
    </div>
  );
}

function VoucherAuditTrail({ auditLog }) {
  return (
    <div className="border-t border-slate-200 p-4">
      <p className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider mb-2">Audit Trail</p>
      <div className="space-y-1.5">
        {auditLog?.map((entry) => (
          <div key={`${entry.action}-${entry.timestamp}`} className="flex items-center gap-2 text-xs">
            <div className="w-1.5 h-1.5 rounded-full bg-slate-300"></div>
            <span className="font-medium text-slate-700">{entry.action}</span>
            <span className="text-slate-400">by {entry.performedByName || '-'}</span>
            <span className="text-slate-400 ml-auto">{formatDate(entry.timestamp)}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function SigBlock({ label, sig, canSign, onSave }) {
  const [showPad, setShowPad] = useState(false);
  const hasSignature = sig?.signatureData;
  const slugLabel = label.toLowerCase().replace(/\s+/g, '-');

  return (
    <div className="border border-slate-200 rounded-md p-2.5 bg-white" data-testid={`sig-block-${slugLabel}`}>
      <p className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider mb-1">{label}</p>
      {hasSignature ? (
        <div>
          <img src={sig.signatureData} alt={label} className="h-14 object-contain" />
          <p className="text-[10px] text-slate-400 mt-1">{sig.name} - {new Date(sig.timestamp).toLocaleDateString('en-IN')}</p>
        </div>
      ) : sig?.name ? (
        <div>
          <p className="text-xs text-slate-700 font-medium">{sig.name}</p>
          <p className="text-[10px] text-slate-400">{sig.timestamp ? new Date(sig.timestamp).toLocaleDateString('en-IN') : ''}</p>
          {canSign && !showPad && (
            <button onClick={() => setShowPad(true)} className="text-[10px] text-slate-600 hover:text-slate-900 underline mt-1" data-testid={`add-sig-${slugLabel}`}>Add signature</button>
          )}
        </div>
      ) : (
        <div>
          <p className="text-[10px] text-slate-400 italic">Not signed</p>
          {canSign && !showPad && (
            <button onClick={() => setShowPad(true)} className="text-[10px] text-slate-600 hover:text-slate-900 underline mt-1" data-testid={`add-sig-${slugLabel}`}>Sign now</button>
          )}
        </div>
      )}
      {showPad && canSign && (
        <div className="mt-2">
          <SignaturePad label={label} onSave={(data) => { onSave(data); setShowPad(false); }} />
        </div>
      )}
    </div>
  );
}
